'use client';

import { useRecurly } from '@recurly/react-recurly';
import { ApplePayInstance } from '@recurly/recurly-js/lib/apple-pay';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { JSX } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { useCheckoutContext } from './CheckoutContext';
import { getBraintreeClientAuthorization } from './actions';
import { usePurchasePreview } from './usePurchasePreview';

import ApplePayError = ApplePayJS.ApplePayError;

export interface ApplePayButtonProps {
    disabled?: boolean;
    onSuccess: (data: { tokenId: string }) => void;
    onError: (error: { code: string; message: string; cause?: unknown }) => void;
}

declare global {
    interface Window {
        ApplePaySession?: typeof ApplePaySession;
    }
}

const logger = LoggerFactory.create('ApplePayButton');

const APPLE_PAY_UNSUPPORTED_ERROR = {
    code: 'APPLE_PAY_UNSUPPORTED',
    message: 'Apple Pay is not available on this device or browser.',
};

const APPLE_PAY_INIT_ERROR = {
    code: 'APPLE_PAY_INIT_ERROR',
    message: 'Failed to initialize Apple Pay. Please try again later.',
};

// Simplified browser detection for Apple Pay support
const getBrowserInfo = () => {
    if (typeof window === 'undefined')
        return { isSafari: false, isMobile: false, isIOS: false, supportsNativeApplePay: false };

    const userAgent = window.navigator.userAgent;
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);

    return {
        isSafari,
        isMobile,
        isIOS,
        supportsNativeApplePay: isSafari && (isIOS || !isMobile),
    };
};

export default function ApplePayButton({
    disabled = false,
    onSuccess,
    onError,
}: ApplePayButtonProps): JSX.Element | null {
    const recurly = useRecurly();
    const { currency } = useCheckoutContext();
    const { total } = usePurchasePreview();

    const [isSupported, setIsSupported] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [applePayInstance, setApplePayInstance] = useState<(ApplePayInstance & { __token?: string }) | null>(null);
    const [configuredTotal, setConfiguredTotal] = useState<number | null>(null);
    const [browserInfo] = useState(getBrowserInfo());

    const displayName = process.env.NEXT_PUBLIC_APPLE_PAY_DISPLAY_NAME || 'Storyblocks';
    const merchantIdentifier = process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID || '';
    const applePayCountryCode = process.env.NEXT_PUBLIC_APPLE_PAY_COUNTRY_CODE || 'US';

    // Use proper refs that persist across renders
    const isMountedRef = useRef(true);
    const hasReportedErrorRef = useRef(false);
    const onErrorRef = useRef(onError);
    const onSuccessRef = useRef(onSuccess);
    const currentCurrencyRef = useRef(currency);

    // Update refs when props change
    useEffect(() => {
        onErrorRef.current = onError;
        onSuccessRef.current = onSuccess;
    }, [onError, onSuccess]);

    useEffect(() => {
        currentCurrencyRef.current = currency;
    }, [currency]);

    // Set mounted state on mount/unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Check for Apple Pay support
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        // Only handle Safari browsers (native Apple Pay support)
        if (browserInfo.supportsNativeApplePay) {
            const ApplePaySession = window.ApplePaySession;

            if (!ApplePaySession || typeof ApplePaySession.canMakePayments !== 'function') {
                setIsSupported(false);
                return;
            }

            try {
                const canPay = ApplePaySession.canMakePayments();

                if (typeof canPay === 'boolean') {
                    setIsSupported(canPay);
                } else if (canPay && typeof (canPay as Promise<typeof canPay>).then === 'function') {
                    void (canPay as Promise<typeof canPay>).then((result: boolean) => {
                        setIsSupported(result);
                    });
                } else {
                    setIsSupported(true);
                }
            } catch (error) {
                setIsSupported(false);
            }
        }
        // All non-Safari browsers are unsupported
        else {
            setIsSupported(false);
        }
    }, [browserInfo.supportsNativeApplePay]);

    // No error reporting for non-Safari browsers - just don't show the button
    // This provides a cleaner UX by not showing error messages for unsupported browsers

    // Initialize Apple Pay
    useEffect(() => {
        if (!isSupported || !recurly || !merchantIdentifier) {
            return;
        }

        // Wait for a valid total amount before initializing
        if (!total || total <= 0) {
            return;
        }

        // Re-initialize Apple Pay when the total amount changes
        // This is necessary because the amount is baked into the Apple Pay configuration
        if (configuredTotal !== null && configuredTotal !== total) {
            // Reset state to trigger re-initialization
            setApplePayInstance(null);
            setIsReady(false);
            setConfiguredTotal(null);
            // Return early to let the next effect run handle the new initialization
            return;
        }

        // Skip if already initialized (original simple logic)
        if (applePayInstance) {
            return;
        }

        // Use a simple active flag to handle cleanup (standard React pattern)
        let active = true;

        async function initializeApplePay() {
            try {
                const clientAuthorization = await getBraintreeClientAuthorization();

                // Check if this effect is still active
                if (!active) {
                    return;
                }

                // Configuration for Apple Pay
                const applePayConfig = {
                    country: applePayCountryCode,
                    currency: currentCurrencyRef.current?.toUpperCase() || 'USD',
                    label: displayName,
                    total: total.toFixed(2), // Now we know total is valid (>0)
                    braintree: {
                        clientAuthorization,
                    },
                };

                const applePay: ApplePayInstance & { __token?: string } = recurly.ApplePay(applePayConfig);

                // Set up event handlers for token and errors
                applePay.on('token', (token: string) => {
                    if (isMountedRef.current && token) {
                        // Store the token for later use
                        applePay.__token = token;
                    }
                });

                applePay.on('error', (error: ApplePayError) => {
                    logger.error({ error: error }, 'Apple Pay emitted an error event');
                    if (isMountedRef.current) {
                        onErrorRef.current({
                            code: error?.code || 'APPLE_PAY_ERROR',
                            message: error?.message || 'An error occurred while using Apple Pay.',
                            cause: error,
                        });
                    }
                });

                // Use a promise to handle the ready callback more reliably
                const readyPromise = new Promise<void>((resolve) => {
                    applePay.ready(() => {
                        resolve();
                    });
                });

                await readyPromise;

                // Check if still active before setting state
                if (!active) {
                    return;
                }

                // Only set state if we're still active after the ready callback
                if (isMountedRef.current) {
                    setApplePayInstance(applePay);
                    setConfiguredTotal(total);
                    setIsReady(true);
                }
            } catch (error) {
                if (isMountedRef.current) {
                    onErrorRef.current(APPLE_PAY_INIT_ERROR);
                }
            }
        }

        void initializeApplePay();

        // Cleanup function
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isSupported,
        total,
        configuredTotal,
        recurly,
        merchantIdentifier,
        applePayCountryCode,
        displayName,
        // Note: applePayInstance intentionally excluded to prevent infinite re-initialization loop
        // The state reset pattern (setApplePayInstance(null)) triggers the next effect run
    ]);

    const handleApplePayClick = useCallback(async () => {
        if (isProcessing || disabled || !isSupported || !applePayInstance || !isReady) {
            return;
        }

        // Validate total amount
        if (!total || total <= 0) {
            onErrorRef.current({
                code: 'INVALID_AMOUNT',
                message: 'Invalid payment amount. Please refresh and try again.',
            });
            return;
        }

        setIsProcessing(true);

        try {
            // Pass just the amount string to begin() for token retrieval to work
            const paymentAmount = total.toFixed(2);

            // Create a promise to wait for the token event
            const tokenPromise = new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Apple Pay token timeout'));
                }, 30000); // 30 second timeout

                // Clean up function to remove all listeners
                const cleanup = () => {
                    clearTimeout(timeout);
                    // Check if off() method exists before calling it (for test compatibility)
                    if (typeof applePayInstance.off === 'function') {
                        applePayInstance.off('token', tokenHandler);
                        // @ts-expect-error recurly provides a complete event but it's not in the types
                        applePayInstance.off('complete', completeHandler);
                        applePayInstance.off('error', errorHandler);
                        applePayInstance.off('cancel', cancelHandler);
                    }
                };

                // Listen for the token event
                const tokenHandler = (token: string) => {
                    cleanup();
                    resolve(token);
                };

                // Listen for completion without token (for cases where token was already set)
                const completeHandler = () => {
                    cleanup();
                    const storedToken = applePayInstance.__token;
                    if (storedToken) {
                        resolve(storedToken);
                    } else {
                        resolve(null);
                    }
                };

                // Listen for error events (including cancellation)
                const errorHandler = (error: ApplePayError) => {
                    cleanup();
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(error);
                };

                // Listen for explicit cancel event
                const cancelHandler = () => {
                    cleanup();
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject({ code: 'apple-pay-canceled', message: 'User cancelled Apple Pay' });
                };

                applePayInstance.once('token', tokenHandler);
                // @ts-expect-error recurly provides a complete event but it's not in the types
                applePayInstance.once('complete', completeHandler);
                applePayInstance.once('error', errorHandler);
                applePayInstance.once('cancel', cancelHandler);
            });

            // Start the Apple Pay flow - pass the amount string
            // @ts-expect-error incorrect types in recurly-js
            const beginResult = applePayInstance.begin(paymentAmount);

            // Wait for the token (from event or begin result)
            const token = ((beginResult as unknown) || (await tokenPromise)) as
                | string
                | { id?: string; tokenId?: string }
                | null;

            // Extract token ID from various possible formats
            let tokenId: string | null = null;
            if (typeof token === 'string') {
                tokenId = token;
            } else if (token?.id) {
                tokenId = token.id;
            } else if (token?.tokenId) {
                tokenId = token.tokenId;
            }

            if (tokenId) {
                logger.info('Apple Pay payment succeeded');
                onSuccessRef.current({ tokenId });
            } else {
                logger.warn('Apple Pay returned without token id');
                onErrorRef.current({
                    code: 'APPLE_PAY_NO_TOKEN',
                    message: 'Apple Pay did not return a payment token. Please try another method.',
                });
            }
        } catch (error: unknown) {
            const isCancelled =
                ((error as ApplePayError)?.code as string) === 'apple-pay-cancelled' ||
                ((error as ApplePayError)?.code as string) === 'apple-pay-canceled';

            if (!isCancelled) {
                onErrorRef.current({
                    code: (error as ApplePayError)?.code || 'APPLE_PAY_ERROR',
                    message: (error as ApplePayError)?.message || 'An error occurred while processing Apple Pay.',
                    cause: error as ApplePayError,
                });
            }
        } finally {
            setIsProcessing(false);
        }
    }, [applePayInstance, disabled, isProcessing, isReady, isSupported, total]);

    if (!isSupported) {
        return null;
    }

    // Show loading state while initializing
    if (!isReady) {
        return (
            <div className="w-full h-12 bg-gray-100 rounded-md flex items-center justify-center">
                <span className="text-sm text-gray-600">Loading Apple Pay...</span>
            </div>
        );
    }
    return (
        <button
            type="button"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleApplePayClick}
            disabled={disabled || isProcessing}
            className={`apple-pay-button w-full py-3 px-4 border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                disabled || isProcessing ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''
            }`}
        >
            {/* Remove any text content - let the native Apple Pay button handle the display */}
        </button>
    );
}
